import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CoupeGeologique from "./CoupeGeologique";

const NATURES_SOL = [
  { groupe: "Sols cohésifs", options: [
    { label: "Argile molle (Su<25 kPa)",    value: "argile_molle",    defaults: { gamma:17, c:10,  phi:12 } },
    { label: "Argile ferme (Su 25–80 kPa)", value: "argile_ferme",    defaults: { gamma:18, c:30,  phi:18 } },
    { label: "Argile raide (Su>80 kPa)",    value: "argile_raide",    defaults: { gamma:19, c:60,  phi:22 } },
    { label: "Limon",                        value: "limon",           defaults: { gamma:17, c:15,  phi:16 } },
    { label: "Marne",                        value: "marne",           defaults: { gamma:21, c:50,  phi:25 } },
  ]},
  { groupe: "Sols grenus", options: [
    { label: "Sable lâche",  value: "sable_lache",  defaults: { gamma:17, c:0,   phi:28 } },
    { label: "Sable dense",  value: "sable_dense",  defaults: { gamma:19, c:0,   phi:35 } },
    { label: "Gravier",      value: "gravier",      defaults: { gamma:20, c:0,   phi:38 } },
    { label: "Remblai",      value: "remblai",      defaults: { gamma:18, c:5,   phi:25 } },
  ]},
  { groupe: "Sols rocheux", options: [
    { label: "Calcaire altéré", value: "calcaire_altere", defaults: { gamma:22, c:100, phi:35 } },
    { label: "Calcaire sain",   value: "calcaire_sain",   defaults: { gamma:25, c:200, phi:40 } },
    { label: "Roche",           value: "roche",           defaults: { gamma:26, c:500, phi:45 } },
  ]},
];

const GROUPES_SOL = NATURES_SOL.map(g => g.groupe);

const ESSAIS_NORM = {
  "Pressiomètre": "pressiometre",
  "Essais labo":  "labo",
  "Aucun essai":  "aucun",
  "CPT":          "cpt",
  "SPT":          "spt",
  "Carottage":    "carottage",
};

const NATURES_NORM = Object.fromEntries(
  NATURES_SOL.flatMap(g => g.options).flatMap(o => [
    [o.label, o.value],
    [o.value, o.value],
  ])
);

const couleurNature = {
  remblai:"#C8A84B",
  argile_molle:"#9BBFE0", argile_ferme:"#7BA3C8", argile_raide:"#4B7BAA",
  limon:"#9BB8A0",
  sable_lache:"#F5E4A0", sable_moyen:"#F0D080", sable_dense:"#D4A840",
  gravier:"#D4B860",
  marne:"#C4956A",
  calcaire_altere:"#C0C0C0", calcaire_sain:"#909090",
  roche:"#707070",
  // Rétrocompatibilité legacy vals
  argile:"#7BA3C8", sable:"#F0D080",
};

// Valeurs corrélatives par nature de sol — pré-remplissage éditable (NF P94-110 + NF P94-113)
const SOL_ESSAIS = {
  remblai:         { pl_etoile: 150,  E_module: 3,   qc_cpt: 0.3,  rdt: "80"  },
  argile_molle:    { pl_etoile: 150,  E_module: 3,   qc_cpt: 0.3,  rdt: "80"  },
  argile_ferme:    { pl_etoile: 300,  E_module: 7,   qc_cpt: 0.8,  rdt: "150" },
  argile_raide:    { pl_etoile: 600,  E_module: 12,  qc_cpt: 1.5,  rdt: "200" },
  limon:           { pl_etoile: 450,  E_module: 8,   qc_cpt: 1.0,  rdt: "150" },
  sable_lache:     { pl_etoile: 500,  E_module: 6,   qc_cpt: 2.0,  rdt: "150" },
  sable_moyen:     { pl_etoile: 1000, E_module: 12,  qc_cpt: 5.0,  rdt: "200" },
  sable_dense:     { pl_etoile: 2000, E_module: 20,  qc_cpt: 10.0, rdt: "300" },
  gravier:         { pl_etoile: 2000, E_module: 20,  qc_cpt: 10.0, rdt: "300" },
  marne:           { pl_etoile: 1500, E_module: 15,  qc_cpt: 4.0,  rdt: "300" },
  calcaire_altere: { pl_etoile: 1500, E_module: 30,  qc_cpt: 8.0,  rdt: "300" },
  calcaire_sain:   { pl_etoile: 3000, E_module: 80,  qc_cpt: 15.0, rdt: "500" },
  roche:           { pl_etoile: 5000, E_module: 100, qc_cpt: 20.0, rdt: "500" },
  // Legacy
  argile:          { pl_etoile: 250,  E_module: 5,   qc_cpt: 0.5,  rdt: "80"  },
  sable:           { pl_etoile: 1000, E_module: 12,  qc_cpt: 5.0,  rdt: "300" },
};

// RDT normatif depuis pl* (Ménard NF P94-261 — approximation)
const rdtFromPl = (pl) => {
  const v = parseFloat(pl);
  if (!v) return null;
  if (v < 300)  return "80";
  if (v < 600)  return "150";
  if (v < 1200) return "200";
  if (v < 2500) return "300";
  return "500";
};

// RDT normatif depuis qc CPT (corrélation Ménard qc/pl*≈3)
const rdtFromQc = (qc) => {
  const pl = parseFloat(qc) * 1000 / 3; // qc MPa → kPa, puis /3
  return rdtFromPl(pl);
};

// RDT théorique depuis c, φ — Terzaghi (bande, FS=3)
const calculerRdtTheorique = (c, phi, gamma = 18, D = 0.8) => {
  const phiR = phi * Math.PI / 180;
  const Nq = Math.exp(Math.PI * Math.tan(phiR)) * Math.pow(Math.tan(Math.PI / 4 + phiR / 2), 2);
  const Nc = phi > 0 ? (Nq - 1) / Math.tan(phiR) : 5.14;
  const Ng = 2 * (Nq + 1) * Math.tan(phiR);
  const q_ult = c * Nc + gamma * D * Nq + 0.5 * gamma * 1 * Ng;
  const q_adm = q_ult / 3;
  if (q_adm < 100) return "80";
  if (q_adm < 200) return "150";
  if (q_adm < 300) return "200";
  if (q_adm < 500) return "300";
  return "500";
};

// Labels contextuels RDT selon la nature de la couche d'assise
const RDT_DESC_NATURE = {
  remblai:         { "80":"Remblai très lâche / non compacté", "150":"Remblai lâche", "200":"Remblai compacté", "300":"Remblai très compacté", "500":"—", "?":"Essais requis" },
  argile_molle:    { "80":"Argile très molle (Su < 25 kPa)", "150":"Argile molle", "200":"—", "300":"—", "500":"—", "?":"Essais requis" },
  argile_ferme:    { "80":"—", "150":"Argile ferme (Su 25–75 kPa)", "200":"Argile ferme à raide", "300":"—", "500":"—", "?":"Essais requis" },
  argile_raide:    { "80":"—", "150":"—", "200":"Argile raide (Su > 75 kPa)", "300":"Argile très raide", "500":"—", "?":"Essais requis" },
  limon:           { "80":"Limon très mou", "150":"Limon mou", "200":"Limon ferme", "300":"Limon dense compacté", "500":"—", "?":"Essais requis" },
  sable_lache:     { "80":"Sable très lâche (Dr < 20%)", "150":"Sable lâche (Dr < 35%)", "200":"—", "300":"—", "500":"—", "?":"Essais requis" },
  sable_moyen:     { "80":"—", "150":"—", "200":"Sable moyen (Dr 35–65%)", "300":"—", "500":"—", "?":"Essais requis" },
  sable_dense:     { "80":"—", "150":"—", "200":"—", "300":"Sable dense (Dr > 65%)", "500":"Sable très dense / cimenté", "?":"Essais requis" },
  gravier:         { "80":"—", "150":"Gravier lâche", "200":"Gravier moyen", "300":"Gravier dense", "500":"Gravier très dense", "?":"Essais requis" },
  marne:           { "80":"Marne très molle", "150":"Marne molle", "200":"Marne ferme", "300":"Marne raide", "500":"Marne très raide", "?":"Essais requis" },
  calcaire_altere: { "80":"—", "150":"—", "200":"Calcaire très altéré", "300":"Calcaire altéré (RQD < 50%)", "500":"Calcaire peu altéré", "?":"Essais requis" },
  calcaire_sain:   { "80":"—", "150":"—", "200":"—", "300":"—", "500":"Calcaire sain (RQD > 75%)", "?":"Essais requis" },
  roche:           { "80":"—", "150":"—", "200":"Roche très altérée", "300":"Roche fracturée (RQD < 50%)", "500":"Roche saine (RQD > 75%)", "?":"Essais requis" },
  // Legacy
  argile:          { "80":"Argile très molle", "150":"Argile molle à ferme", "200":"Argile ferme", "300":"Argile raide", "500":"Argile très raide", "?":"Essais requis" },
  sable:           { "80":"Sable très lâche", "150":"Sable lâche", "200":"Sable moyennement dense", "300":"Sable dense", "500":"Sable très dense", "?":"Essais requis" },
};
const RDT_DESC_DEFAUT = { "80":"Sol mou — pieux obligatoires", "150":"Sol peu portant — à justifier", "200":"Sol moyen — essais requis", "300":"Bon sol portant", "500":"Très bon sol / roche", "?":"Essais à réaliser" };

const valDefautCouche = (nature) => {
  const o = NATURES_SOL.flatMap(g => g.options).find(x => x.value === nature);
  if (!o) return { gamma:18, cohesion:0, phi:25 };
  return { gamma: o.defaults.gamma, cohesion: o.defaults.c, phi: o.defaults.phi };
};

const FORM_DEFAUT = {
  typeOuvrage: "", hauteur: "", longueur: "", zoneUsage: "",
  zoneSismique: "", surcharge: "",
  typeSol: "", cohesion: "", frottement: "",
  rdt: "", nappe: "", nappeProf: "",
  profondeur_encastrement: "",
  essais: [], batimentVoisin: false, batimentVoisinDistance: "",
  espaceDisponible: "", delai: "", budget: "",
  servitudeTrefonds: "inconnue", avoisinantsSensibles: false,
  pl_etoile: "", E_module: "", qc_cpt: "",
};

function PageProjet({ onEnter, setDonneesProjet, etapeInitiale = 0, onEtapeChange, donneesInitiales, pays: paysProp, onAnalyser }) {
  const { t } = useTranslation();
  const [etape, setEtape] = useState(etapeInitiale);

  // Synchroniser quand le menu global change d'étape
  useEffect(() => { setEtape(etapeInitiale); }, [etapeInitiale]);

  // Scroll vers le haut à chaque changement d'étape
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [etape]);

  // Couches — initialisées depuis donneesInitiales si disponibles
  const [couches, setCouches] = useState(() => {
    if (donneesInitiales?.couches?.length > 0) return donneesInitiales.couches;
    return [{ id:1, nom:"Couche 1", nature:"", profondeur_toit:0, profondeur_base:3, gamma:18, cohesion:0, phi:25 }];
  });

  const ajouterCouche = () => {
    const derniere = couches[couches.length - 1];
    const base = derniere ? derniere.profondeur_base : 0;
    const def = valDefautCouche("");
    setCouches(c => [...c, { id:Date.now(), nom:`Couche ${c.length+1}`, nature:"", profondeur_toit:base, profondeur_base:base+2, ...def }]);
  };

  const supprimerCouche = (id) => { if (couches.length > 1) setCouches(c => c.filter(x => x.id !== id)); };

  const modifierCouche = (id, champ, valeur) => {
    setCouches(c => c.map(x => {
      if (x.id !== id) return x;
      if (champ === "nature") {
        const def = valDefautCouche(valeur);
        return { ...x, nature: valeur, gamma: def.gamma, cohesion: def.cohesion, phi: def.phi };
      }
      return { ...x, [champ]: valeur };
    }));
  };

  // Recalcul automatique des profondeurs toits après chaque modification
  const couchesNormalisees = couches.map((c, i) => ({
    ...c,
    profondeur_toit: i === 0 ? 0 : parseFloat(couches[i-1].profondeur_base) || 0,
  }));

  // Formulaire — initialisé depuis donneesInitiales si disponibles
  const [form, setForm] = useState(() => {
    if (donneesInitiales) {
      const { couches: _c, solutions: _s, alertes_projet: _a, ...formData } = donneesInitiales;
      const preRempli = { ...FORM_DEFAUT, ...formData };
      // Auto-remplir zone sismique depuis la wilaya du client si non définie
      if (!preRempli.zoneSismique && donneesInitiales.clientInfo?.wilaya?.zone) {
        preRempli.zoneSismique = donneesInitiales.clientInfo.wilaya.zone;
      }
      return preRempli;
    }
    return { ...FORM_DEFAUT };
  });

  // Couche d'assise = couche la plus profonde (hors remblai si possible)
  const coucheAssise = couchesNormalisees.length > 1
    ? (couchesNormalisees.slice().reverse().find(c => c.nature !== "remblai") || couchesNormalisees[couchesNormalisees.length - 1])
    : couchesNormalisees[0];
  const rdtSuggere = coucheAssise ? SOL_ESSAIS[coucheAssise.nature]?.rdt : null;
  const rdtRatio = (form.rdt && form.rdt !== "?" && rdtSuggere) ? parseInt(form.rdt) / parseInt(rdtSuggere) : 1;
  const rdtIncohérent = form.rdt && form.rdt !== "?" && rdtSuggere && (rdtRatio > 2 || rdtRatio < 0.4);

  // RDT calculé depuis c et φ de la couche d'assise (Terzaghi, FS=3)
  const rdtTheorique = coucheAssise
    ? calculerRdtTheorique(
        parseFloat(coucheAssise.cohesion) ?? 0,
        parseFloat(coucheAssise.phi) ?? 25,
        parseFloat(coucheAssise.gamma) || 18,
        parseFloat(form.profondeur_encastrement) || 0.8
      )
    : null;
  const BUCKETS = ["80","150","200","300","500"];
  const bucketDiff = (form.rdt && form.rdt !== "?" && rdtTheorique)
    ? Math.abs(BUCKETS.indexOf(form.rdt) - BUCKETS.indexOf(rdtTheorique)) : 0;
  const rdtCisaillementIncohérent = bucketDiff >= 2;

  // Règle FR-S018 : encastrement en fiche courte pour pieux (Calcaire / Roche)
  const NATURES_ROCHEUX = ["calcaire_sain","calcaire_altere","roche"];
  const regleFS018Active = coucheAssise && NATURES_ROCHEUX.includes(coucheAssise.nature);

  // ── Lien Page 2 → Page 3 : distance bâtiment ↔ espace disponible ──
  const distVoisin  = parseFloat(form.batimentVoisinDistance) || null;
  const hauteurOuvrage = parseFloat(form.hauteur) || 4;
  const espaceAutoDetecte = distVoisin !== null
    ? (distVoisin < 1 ? "contraint" : distVoisin < Math.max(hauteurOuvrage, 4) ? "limite" : null)
    : null;

  // Auto-sélection quand le champ est vide et qu'une distance est saisie
  useEffect(() => {
    if (espaceAutoDetecte && !form.espaceDisponible) {
      inp("espaceDisponible", espaceAutoDetecte);
    }
  }, [espaceAutoDetecte]); // eslint-disable-line react-hooks/exhaustive-deps

  const ORDRE_ESPACE = { contraint:0, limite:1, normal:2, large:3 };
  const espaceContradiction = espaceAutoDetecte && form.espaceDisponible &&
    (ORDRE_ESPACE[form.espaceDisponible] ?? 4) > (ORDRE_ESPACE[espaceAutoDetecte] ?? 4);

  const estSoutFond    = form.typeOuvrage?.startsWith("soutenement") || form.typeOuvrage?.startsWith("fondation");
  const milieuUrbainActif = form.zoneUsage === "Milieu urbain (contrainte d'emprise)";
  const espaceManquant = estSoutFond && !form.espaceDisponible && !milieuUrbainActif;

  const GV = {
    vert:"#2D7A2D", vertClair:"#00A651", vertLight:"#E8F5E9", vertMid:"#4CAF50",
    vertDark:"#1B5E20", blanc:"#FFFFFF", gris:"#F5F5F5", grisMid:"#757575",
    dark:"#1A1A1A", border:"#E0E0E0", orange:"#E8601A", orangeLight:"#FFF3ED",
    rouge:"#DC2626", rougeLight:"#FEF2F2",
  };

  const ETAPES = [
    { id:0, label:`1. ${t("projet.etape1")}`, icon:"🏗️" },
    { id:1, label:`2. ${t("projet.etape2")}`, icon:"⛰️" },
    { id:2, label:`3. ${t("projet.etape3")}`, icon:"📋" },
  ];

  // Natures du sol traduites (utilisées dans le JSX)
  const NATURES_SOL_LABELS = t("pageprojet.natures_sol", { returnObjects: true }) || [];
  const _nsFlat = NATURES_SOL.flatMap(g => g.options);
  const NATURES_SOL_TR = NATURES_SOL.map(g => ({
    ...g,
    options: g.options.map(o => ({ ...o, label: NATURES_SOL_LABELS[_nsFlat.indexOf(o)] || o.label })),
  }));

  const TYPES_OUVRAGE = [
    { val:"soutenement_remblai",    label:"Soutènement de remblai",         icon:"🧱", ex:"Mur de soutènement, remblai routier",          help:"À choisir lorsque l'ouvrage retient des terres rapportées ou un remblai créé au-dessus du terrain naturel : plateforme, voirie, rampe, terrassement en remblai, élargissement routier." },
    { val:"soutenement_deblai",     label:"Soutènement de déblai / fouille",icon:"⛏️", ex:"Parking souterrain, fouille de bâtiment",       help:"À choisir lorsqu'on excave le terrain naturel et qu'il faut maintenir une paroi de fouille : sous-sol, parking enterré, tranchée profonde, excavation urbaine ou ouvrage en déblai." },
    { val:"talus_stabilisation",    label:"Stabilisation de talus",          icon:"⛰️", ex:"Talus instable, glissement de terrain",         help:"À choisir lorsqu'il s'agit de sécuriser un talus existant, naturel ou artificiel, sans créer nécessairement une paroi verticale : glissement, érosion, instabilité de pente." },
    { val:"fondation_batiment",     label:"Fondation de bâtiment",           icon:"🏢", ex:"Immeuble R+5, villa, infrastructure",           help:"À choisir pour transmettre les charges d'un bâtiment ou d'un ouvrage au sol : semelles, radier, pieux, micropieux, selon portance, tassements et profondeur du bon sol." },
    { val:"amelioration_sol",       label:"Amélioration de sol",             icon:"🔬", ex:"Traitement de sol mou, consolidation, injection",help:"À choisir lorsque le problème principal est d'améliorer les caractéristiques du sol avant construction : portance, tassements, compressibilité, drainage, densification ou renforcement." },
    { val:"infrastructure_lineaire",label:"Infrastructure linéaire",         icon:"🛣️", ex:"Route, voie ferrée, canal",                     help:"À choisir pour les ouvrages linéaires : routes, voies ferrées, plateformes, remblais, déblais, murs associés, ouvrages d'art ou aménagements de grande longueur." },
  ];

  // Types d'ouvrage traduits (après la définition de TYPES_OUVRAGE)
  const TYPES_OUVRAGE_T_DATA = t("pageprojet.types_ouvrage", { returnObjects: true }) || [];
  const TYPES_OUVRAGE_TR = TYPES_OUVRAGE.map((ov, i) => ({
    ...ov,
    label: TYPES_OUVRAGE_T_DATA[i]?.label || ov.label,
    ex:    TYPES_OUVRAGE_T_DATA[i]?.ex    || ov.ex,
  }));

  const pays = paysProp || donneesInitiales?.clientInfo?.pays || "france";

  // Zones sismiques DZ (RPA 2024) — conservé pour compatibilité licence DZ
  const ZONES_SISMIQUES_DZ = [
    { val:"0",  label:"Zone 0",  desc:"Négligeable — Grand Sud (Adrar, Ouargla...)" },
    { val:"I",  label:"Zone I",  desc:"Faible — A=0.07g (Béchar, Ghardaïa, El Oued)" },
    { val:"II", label:"Zone II", desc:"Faible à Moyenne — A=0.10g (Laghouat, Djelfa, Naâma)" },
    { val:"III",label:"Zone III",desc:"Moyenne — A=0.15g (Tlemcen, Biskra, Tébessa)" },
    { val:"IV", label:"Zone IV", desc:"Moyenne à Élevée — A=0.20g (Oran, Batna, M'Sila)" },
    { val:"V",  label:"Zone V",  desc:"Élevée — A=0.25g (Chlef, Tizi Ouzou, Sétif)" },
    { val:"VI", label:"Zone VI", desc:"Élevée Maximale — A=0.30g (Alger, Blida, Boumerdès)" },
  ];

  const ZONES_SISMIQUES_FR = [
    { val:"1", label:"Zone 1", desc:"Très faible — agR=0.4 m/s² (Bassin parisien)" },
    { val:"2", label:"Zone 2", desc:"Faible — agR=0.7 m/s² (Massif Central)" },
    { val:"3", label:"Zone 3", desc:"Modérée — agR=1.1 m/s² (Alpes Nord, Alsace)" },
    { val:"4", label:"Zone 4", desc:"Moyenne — agR=1.6 m/s² (Alpes Sud, Pyrénées)" },
    { val:"5", label:"Zone 5", desc:"Forte — agR=3.0 m/s² (Antilles)" },
  ];

  const ZONES_SISMIQUES = pays === "france" ? ZONES_SISMIQUES_FR : ZONES_SISMIQUES_DZ;

  // kh France : NF EN 1998-5 §7.3.2.2 — kh = α·S/r (S=1.40 cl.E, r=1.5) | kh DZ : RPA 2024 §3.2
  const KH_MAP = pays === "france"
    ? {"1":"0.037","2":"0.065","3":"0.103","4":"0.149","5":"0.280"}
    : {"0":"0","I":"0.07","II":"0.10","III":"0.15","IV":"0.20","V":"0.25","VI":"0.30"};

  const SURCHARGES = [
    { val:"0",  label:"Aucune",         desc:"Terrain vierge" },
    { val:"5",  label:"q = 5 kN/m²",   desc:"Piétons, jardins" },
    { val:"10", label:"q = 10 kN/m²",  desc:"Trafic VL, stationnement" },
    { val:"20", label:"q = 20 kN/m²",  desc:"Trafic PL, route" },
    { val:"30", label:"q = 30 kN/m²",  desc:"⚠️ Lourd — entrepôt, camion" },
    { val:"50", label:"q ≥ 50 kN/m²",  desc:"🔴 Très lourd — industrie" },
  ];

  const RDT_OPTIONS = [
    { val:"80",  label:"< 100 kPa",    desc:"Argile molle — pieux obligatoires" },
    { val:"150", label:"100–200 kPa",  desc:"Limon argileux — à justifier" },
    { val:"200", label:"200–300 kPa",  desc:"Sol moyen — essais requis" },
    { val:"300", label:"300–500 kPa",  desc:"Bon sol — sable dense" },
    { val:"500", label:"> 500 kPa",    desc:"Très bon sol — roche" },
    { val:"?",   label:"Inconnu",      desc:"Essais à réaliser" },
  ];


  const allerEtape = (n) => { setEtape(n); if (onEtapeChange) onEtapeChange(n); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const inp = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleEssai = (e) => setForm(f => ({ ...f, essais: f.essais.includes(e) ? f.essais.filter(x=>x!==e) : [...f.essais, e] }));

  const isSoutenement        = ["soutenement_remblai","soutenement_deblai"].includes(form.typeOuvrage);
  const showSurcharge        = ["soutenement_remblai","soutenement_deblai","infrastructure_lineaire"].includes(form.typeOuvrage);
  // FIX-D.a : D toujours visible — label et description adaptés selon le type
  const labelProfondeurD = {
    fondation_batiment:      "Profondeur d'assise D (m/TN)",
    soutenement_remblai:     "Profondeur de base de l'ouvrage D (m/TN)",
    soutenement_deblai:      "Profondeur de base de l'ouvrage D (m/TN)",
    soutenement_fouille:     "Profondeur de base de l'ouvrage D (m/TN)",
    stabilisation_talus:     "Profondeur de référence D (m/TN)",
    infrastructure_lineaire: "Profondeur d'assise D (m/TN)",
    amelioration_sol:        "Profondeur de traitement D (m/TN)",
  }[form.typeOuvrage] || "Profondeur D (m/TN)";
  const showBatimentSensible    = isSoutenement;
  const showServitudeTrefonds   = isSoutenement;
  const showAvoisinantsSensibles = isSoutenement || form.typeOuvrage === "fondation_batiment";
  const isSeismiqueEleve = pays === "france"
    ? ["3","4","5"].includes(form.zoneSismique)
    : ["III","IV","V","VI"].includes(form.zoneSismique);
  const alerteSurchargeSeisme = isSoutenement && isSeismiqueEleve && ["30","50"].includes(form.surcharge);

  // FIX-D.f — D obligatoire et > 0 sur toutes les étapes qui affichent le champ
  const dValide = parseFloat(form.profondeur_encastrement) > 0;

  const canNext = () => {
    if (etape === 0) return form.typeOuvrage && form.hauteur && form.zoneSismique;
    if (etape === 1) return couches.length > 0 && couches.every(c => c.nature !== "") && dValide;
    if (etape === 2) return !espaceManquant;
    return true;
  };

  const labelSt = { fontSize:13, fontWeight:700, color:GV.dark, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:10 };
  const labelSm = { fontSize:13, fontWeight:700, color:GV.dark, display:"block", marginBottom:6 };
  const inpSt   = { width:"100%", padding:"11px 13px", fontSize:14, border:`1.5px solid ${GV.border}`, borderRadius:8, outline:"none", boxSizing:"border-box", background:GV.blanc };

  const CarteChoix = ({ valeur, label, icon, desc, ex, help, champ }) => {
    const sel = form[champ] === valeur;
    const [hovered, setHovered] = useState(false);
    return (
      <div
        onClick={() => inp(champ, valeur)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ position:"relative", border:`2px solid ${sel?GV.vert:GV.border}`, background:sel?GV.vertLight:GV.blanc, borderRadius:12, padding:"14px 16px", cursor:"pointer", transition:"all 0.18s", boxShadow:sel?`0 0 0 3px ${GV.vert}25`:"0 1px 4px rgba(0,0,0,0.06)" }}
      >
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:(desc||ex)?5:0 }}>
          <span style={{ fontSize:22 }}>{icon}</span>
          <span style={{ fontSize:13, fontWeight:700, color:sel?GV.vertDark:GV.dark, flex:1 }}>{label}</span>
          {sel && <span style={{ color:GV.vert, fontSize:16 }}>✓</span>}
        </div>
        {(desc||ex) && <div style={{ fontSize:11, color:GV.grisMid, paddingLeft:32 }}>{desc||ex}</div>}
        {hovered && help && (
          <div style={{
            position:"absolute", bottom:"calc(100% + 8px)", left:"50%",
            transform:"translateX(-50%)",
            background:"#1e293b", color:"#e2e8f0",
            borderRadius:10, padding:"10px 14px",
            fontSize:12, width:260, maxWidth:"90vw",
            zIndex:999, lineHeight:1.6,
            pointerEvents:"none", boxShadow:"0 8px 32px #0008",
            whiteSpace:"normal"
          }}>{help}</div>
        )}
      </div>
    );
  };

  const BtnChoix = ({ valeur, label, desc, champ, cActif }) => {
    const sel = form[champ] === valeur;
    const c = cActif || GV.vert;
    return (
      <button onClick={() => inp(champ, valeur)} style={{ padding:"9px 14px", borderRadius:8, fontSize:12, cursor:"pointer", border:`1.5px solid ${sel?c:GV.border}`, background:sel?c+"18":GV.blanc, color:sel?c:GV.dark, fontWeight:sel?700:400, textAlign:"left", transition:"all 0.15s", display:"flex", flexDirection:"column", gap:2 }}>
        <span style={{ fontWeight:700, fontSize:13 }}>{label}</span>
        {desc && <span style={{ fontSize:11, color:sel?c:GV.grisMid, fontWeight:400 }}>{desc}</span>}
      </button>
    );
  };

  const Alerte = ({ texte, type="warning" }) => {
    const cfg = { warning:{bg:"#FFF7ED",border:"#F59E0B",color:"#92400E",icon:"⚠️"}, danger:{bg:GV.rougeLight,border:GV.rouge,color:GV.rouge,icon:"🔴"}, info:{bg:"#EFF6FF",border:"#3B82F6",color:"#1E40AF",icon:"ℹ️"} }[type];
    return (
      <div style={{ background:cfg.bg, border:`1.5px solid ${cfg.border}`, borderRadius:10, padding:"12px 16px", marginTop:10, marginBottom:4, display:"flex", gap:10, alignItems:"flex-start" }}>
        <span style={{ flexShrink:0 }}>{cfg.icon}</span>
        <span style={{ fontSize:13, color:cfg.color, lineHeight:1.6 }}>{texte}</span>
      </div>
    );
  };

  return (
    <div style={{ fontFamily:"'Segoe UI', system-ui, sans-serif", background:GV.blanc, minHeight:"100vh" }}>

      {/* CONTENU */}
      <div style={{ maxWidth:960, margin:"0 auto", padding:"36px 24px" }}>

        {/* ── ÉTAPE 1 : PROJET ── */}
        {etape === 0 && (
          <div>
            <h2 style={{ fontSize:24, fontWeight:800, color:GV.dark, margin:"0 0 6px" }}>{t("pageprojet.etape1_titre")}</h2>
            <p style={{ fontSize:14, color:GV.grisMid, margin:"0 0 16px" }}>{t("pageprojet.etape1_hint")}</p>

            {/* Type d'ouvrage */}
            <div style={{ marginBottom:28 }}>
              <label style={labelSt}>Type d'ouvrage *</label>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
                {TYPES_OUVRAGE_TR.map(ov => <CarteChoix key={ov.val} valeur={ov.val} label={ov.label} icon={ov.icon} ex={ov.ex} help={ov.help} champ="typeOuvrage"/>)}
              </div>
            </div>

            {/* Hauteur + Longueur */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:28 }}>
              <div>
                <label style={labelSm}>Hauteur / Profondeur (m) *</label>
                <input type="number" min="0" step="0.5" value={form.hauteur} onChange={e=>inp("hauteur",e.target.value)} placeholder="ex: 5.00" style={{ ...inpSt, border:`1.5px solid ${form.hauteur?GV.vert:GV.border}` }}/>
                <div style={{ fontSize:11, color:GV.grisMid, marginTop:4 }}>Mur : hauteur totale / Bâtiment : profondeur fondation</div>
              </div>
              <div>
                <label style={labelSm}>Longueur linéaire (m) — optionnel</label>
                <input type="number" min="0" step="1" value={form.longueur} onChange={e=>inp("longueur",e.target.value)} placeholder="ex: 50" style={inpSt}/>
                <div style={{ fontSize:11, color:GV.grisMid, marginTop:4 }}>Pour estimation du coût total</div>
              </div>
            </div>

            {/* Zone sismique — critique dès étape 1 */}
            <div style={{ marginBottom:28 }}>
              <label style={labelSt}>
                {pays === "france" ? "Zone sismique EC8 (NF EN 1998) *" : "Zone sismique RPA 2024 *"}
                <span style={{ fontSize:11, fontWeight:400, color:GV.grisMid, textTransform:"none", letterSpacing:0, marginLeft:8 }}>— détermine kh et les FS sismiques obligatoires</span>
              </label>
              <div style={{ display:"grid", gridTemplateColumns: pays === "france" ? "repeat(3,1fr)" : "repeat(4,1fr)", gap:10 }}>
                {ZONES_SISMIQUES.map(z => (
                  <BtnChoix key={z.val} valeur={z.val} label={z.label} desc={z.desc} champ="zoneSismique"
                    cActif={
                      pays === "france"
                        ? (z.val === "5" ? GV.rouge : z.val === "4" ? GV.orange : z.val === "3" ? GV.orange : GV.vert)
                        : (["V","VI"].includes(z.val) ? GV.rouge : ["III","IV"].includes(z.val) ? GV.orange : GV.vert)
                    }/>
                ))}
              </div>
              {isSeismiqueEleve && (
                <div style={{ marginTop:10, background:"#FFF7ED", border:"1px solid #F59E0B", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#92400E" }}>
                  {pays === "france"
                    ? `⚠️ Zone ${form.zoneSismique} EC8 — kh=${KH_MAP[form.zoneSismique]} · Vérification NF EN 1998-5 obligatoire · FS sismique ≥ 1.10`
                    : (() => {
                        const z = form.zoneSismique;
                        const A = KH_MAP[z];
                        const kvFormule = ["IV","V","VI"].includes(z) ? "Kv = ±1/2×Kh" : "Kv = ±1/3×Kh";
                        return `⚠️ Zone ${z} — A=${A}g · Kh = 1/3×A×I×S · ${kvFormule} · Phase sismique obligatoire · FS talus ≥ 1.15`;
                      })()
                  }
                </div>
              )}
            </div>

            {/* Surcharge — soutènement et infrastructure linéaire */}
            {showSurcharge && (
              <div style={{ marginBottom:28 }}>
                <label style={labelSt}>
                  Surcharge derrière l'ouvrage
                  <span style={{ fontSize:11, fontWeight:400, color:GV.grisMid, textTransform:"none", letterSpacing:0, marginLeft:8 }}>— impact direct sur le glissement sismique</span>
                </label>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
                  {SURCHARGES.map(s => (
                    <BtnChoix key={s.val} valeur={s.val} label={s.label} desc={s.desc} champ="surcharge"
                      cActif={["30","50"].includes(s.val)?GV.orange:GV.vert}/>
                  ))}
                </div>
                {alerteSurchargeSeisme && (
                  <Alerte
                    texte={pays === "france"
                      ? `Combinaison critique : surcharge ${form.surcharge} kN/m² + Zone ${form.zoneSismique} EC8. Vérifier Mononobe-Okabe (NF EN 1998-5) — FS glissement sismique à justifier. Bêche ou ancrage conseillé.`
                      : `Combinaison critique : surcharge ${form.surcharge} kN/m² + Zone ${form.zoneSismique}. Cas réel identique (q=30, Zone III) → SFs glissement sismique = 1.34, marge = 7% seulement. Prévoir une bêche 0.3–0.5m dès la conception.`
                    }
                    type="warning"
                  />
                )}
              </div>
            )}

            {/* Zone d'usage */}
            <div style={{ marginBottom:8 }}>
              <label style={labelSm}>Localisation / Zone d'usage</label>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                {["Milieu urbain (contrainte d'emprise)","Zone routière","Zone industrielle","Zone résidentielle","Site naturel / rural"].map(z => (
                  <button key={z} onClick={()=>inp("zoneUsage",z)} style={{ padding:"8px 16px", borderRadius:99, fontSize:13, cursor:"pointer", border:`1.5px solid ${form.zoneUsage===z?GV.vert:GV.border}`, background:form.zoneUsage===z?GV.vertLight:GV.blanc, color:form.zoneUsage===z?GV.vertDark:GV.dark, fontWeight:form.zoneUsage===z?700:400 }}>{z}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 2 : TERRAIN ── */}
        {etape === 1 && (
          <div>
            <h2 style={{ fontSize:24, fontWeight:800, color:GV.dark, margin:"0 0 6px" }}>{t("pageprojet.etape2_titre")}</h2>
            <p style={{ fontSize:14, color:GV.grisMid, margin:"0 0 28px" }}>{t("pageprojet.etape2_hint")}</p>

            {/* ══ Essais géotechniques disponibles ══ */}
            <div style={{ marginBottom:24 }}>
              <label style={labelSt}>Essais géotechniques disponibles</label>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:12 }}>
                {["CPT","SPT","Pressiomètre","Carottage","Essais labo","Aucun essai"].map(e => {
                  const sel = form.essais.includes(e);
                  const isPrimaire = ["CPT","Pressiomètre"].includes(e);
                  return (
                    <button key={e} onClick={() => {
                      if (e !== "Aucun essai" && form.essais.includes("Aucun essai")) {
                        setForm(f => ({ ...f, essais: [e] }));
                      } else if (e === "Aucun essai") {
                        setForm(f => ({ ...f, essais: sel ? [] : ["Aucun essai"], pl_etoile:"", E_module:"", qc_cpt:"" }));
                      } else {
                        toggleEssai(e);
                      }
                    }} style={{
                      padding:"8px 16px", borderRadius:8, fontSize:13, cursor:"pointer",
                      border:`1.5px solid ${sel ? (isPrimaire ? "#1D4ED8" : GV.vert) : GV.border}`,
                      background: sel ? (isPrimaire ? "#EFF6FF" : GV.vertLight) : GV.blanc,
                      color: sel ? (isPrimaire ? "#1D4ED8" : GV.vertDark) : GV.dark,
                      fontWeight: sel ? 700 : 400,
                    }}>
                      {sel ? "✓ " : ""}{e}
                      {isPrimaire && <span style={{ fontSize:10, marginLeft:5, opacity:0.7 }}>{e==="Pressiomètre" ? "(pl*·EM)" : "(qc)"}</span>}
                    </button>
                  );
                })}
              </div>

              {/* Champs Pressiomètre */}
              {form.essais.includes("Pressiomètre") && (
                <div style={{ background:"#EFF6FF", border:"1.5px solid #93C5FD", borderRadius:10, padding:"14px 16px", marginBottom:10 }}>
                  <div style={{ fontSize:11, fontWeight:800, color:"#1D4ED8", textTransform:"uppercase", letterSpacing:0.8, marginBottom:10 }}>
                    Paramètres Pressiométriques (NF P94-110)
                    {form.typeSol && SOL_ESSAIS[form.typeSol] && (
                      <span style={{ fontSize:10, color:"#6B7280", fontWeight:400, marginLeft:8, textTransform:"none" }}>
                        — Valeurs corrélatives {form.typeSol} pré-remplies · éditables
                      </span>
                    )}
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                    <div>
                      <label style={{ fontSize:11, color:"#1D4ED8", fontWeight:700, display:"block", marginBottom:4 }}>p<sub>l</sub>* — Pression limite nette (kPa)</label>
                      <input type="number" min="0" step="50"
                        value={form.pl_etoile}
                        onChange={e => { const v=e.target.value; const rc=rdtFromPl(v); setForm(f=>({...f,pl_etoile:v,...(rc?{rdt:rc}:{})})); }}
                        placeholder={SOL_ESSAIS[form.typeSol]?.pl_etoile ?? "ex: 1000"}
                        style={{ width:"100%", padding:"8px 12px", borderRadius:8, border:"1.5px solid #93C5FD", fontSize:13, outline:"none", boxSizing:"border-box" }}
                      />
                      {form.pl_etoile && <div style={{ fontSize:11, color:"#1D4ED8", marginTop:4 }}>→ Rdt normatif : <strong>{rdtFromPl(form.pl_etoile)} kPa</strong></div>}
                    </div>
                    <div>
                      <label style={{ fontSize:11, color:"#1D4ED8", fontWeight:700, display:"block", marginBottom:4 }}>E<sub>M</sub> — Module pressiométrique (MPa)</label>
                      <input type="number" min="0" step="1"
                        value={form.E_module}
                        onChange={e => inp("E_module", e.target.value)}
                        placeholder={SOL_ESSAIS[form.typeSol]?.E_module ?? "ex: 12"}
                        style={{ width:"100%", padding:"8px 12px", borderRadius:8, border:"1.5px solid #93C5FD", fontSize:13, outline:"none", boxSizing:"border-box" }}
                      />
                      {form.E_module && <div style={{ fontSize:11, color:"#6B7280", marginTop:4 }}>EM = {form.E_module} MPa → tassements élastiques (NF P94-261 §6)</div>}
                    </div>
                  </div>
                </div>
              )}

              {/* Champs CPT */}
              {form.essais.includes("CPT") && (
                <div style={{ background:"#EFF6FF", border:"1.5px solid #93C5FD", borderRadius:10, padding:"14px 16px", marginBottom:10 }}>
                  <div style={{ fontSize:11, fontWeight:800, color:"#1D4ED8", textTransform:"uppercase", letterSpacing:0.8, marginBottom:10 }}>
                    Résistance de pointe CPT (NF P94-113)
                  </div>
                  <div style={{ maxWidth:280 }}>
                    <label style={{ fontSize:11, color:"#1D4ED8", fontWeight:700, display:"block", marginBottom:4 }}>q<sub>c</sub> — Résistance de pointe (MPa)</label>
                    <input type="number" min="0" step="0.5"
                      value={form.qc_cpt}
                      onChange={e => { const v=e.target.value; const rc=rdtFromQc(v); setForm(f=>({...f,qc_cpt:v,...(rc?{rdt:rc}:{})})); }}
                      placeholder={SOL_ESSAIS[form.typeSol]?.qc_cpt ?? "ex: 5.0"}
                      style={{ width:"100%", padding:"8px 12px", borderRadius:8, border:"1.5px solid #93C5FD", fontSize:13, outline:"none", boxSizing:"border-box" }}
                    />
                    {form.qc_cpt && (
                      <div style={{ fontSize:11, color:"#1D4ED8", marginTop:4 }}>
                        → Rdt normatif : <strong>{rdtFromQc(form.qc_cpt)} kPa</strong>
                        <span style={{ color:"#6B7280", marginLeft:6 }}>(via pl* ≈ qc/3)</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bandeau Aucun essai */}
              {form.essais.includes("Aucun essai") && (
                <div style={{ marginTop:4, background:"#FFF7ED", border:"2px solid #F97316", borderRadius:10, padding:"12px 16px", fontSize:13, color:"#7C2D12" }}>
                  <div style={{ fontWeight:800, fontSize:13, marginBottom:6 }}>⚠ Analyse basée sur des valeurs corrélatives</div>
                  <div style={{ lineHeight:1.7 }}>
                    Sans essais in-situ (PMT, CPT, SPT), les paramètres sont estimés par corrélation.
                    Les <strong>coefficients de sécurité sont automatiquement majorés</strong> (NF P94-500 + NF EN 1997-1 §2.4).
                    Classe de site EC8 imposée à <strong>E (cas le plus défavorable)</strong>.
                  </div>
                  <div style={{ marginTop:8, fontSize:12, color:"#92400E" }}>→ Dossier non validable par le bureau de contrôle sans investigation géotechnique (NF P94-500).</div>
                </div>
              )}
            </div>

            {/* ── COUCHES + COUPE GÉOLOGIQUE ── */}
            <div style={{ display:"flex", gap:24, marginBottom:32, alignItems:"flex-start" }}>

              {/* Tableau des couches */}
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                  <label style={labelSt}>Coupe stratigraphique</label>
                  <button onClick={ajouterCouche} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", background:GV.vert, border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                    {t("pageprojet.ajouter_couche")}
                  </button>
                </div>

                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {couchesNormalisees.map((c, i) => (
                    <div key={c.id} style={{ border:`1.5px solid ${!c.nature ? GV.rouge : (couleurNature[c.nature] || GV.border)}`, borderLeft:`5px solid ${!c.nature ? GV.rouge : (couleurNature[c.nature] || GV.border)}`, borderRadius:10, padding:"12px 14px", background:"#fafafa" }}>
                      <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8, flexWrap:"wrap" }}>
                        <span style={{ fontWeight:700, fontSize:13, color:GV.dark, minWidth:24 }}>{i+1}.</span>
                        <input value={c.nom} onChange={e => modifierCouche(c.id,"nom",e.target.value)}
                          style={{ flex:1, minWidth:100, padding:"5px 8px", fontSize:13, fontWeight:600, border:`1px solid ${GV.border}`, borderRadius:6, outline:"none" }}/>
                        <select value={c.nature} onChange={e => modifierCouche(c.id,"nature",e.target.value)}
                          style={{ padding:"5px 8px", fontSize:12, border:`1px solid ${!c.nature ? GV.rouge : GV.border}`, borderRadius:6, background:"#fff", cursor:"pointer" }}>
                          <option value="">— Sélectionner le type de sol —</option>
                          {NATURES_SOL_TR.map(grp => (
                            <optgroup key={grp.groupe} label={grp.groupe}>
                              {grp.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </optgroup>
                          ))}
                        </select>
                        {couches.length > 1 && (
                          <button onClick={() => supprimerCouche(c.id)} style={{ background:"#FEF2F2", border:"1px solid #DC2626", color:"#DC2626", borderRadius:6, padding:"4px 8px", cursor:"pointer", fontSize:12 }}>🗑️</button>
                        )}
                      </div>
                      {!c.nature && (
                        <div style={{ fontSize:11, color:GV.rouge, fontWeight:600, marginBottom:6 }}>
                          ⚠ Sélectionner le type de sol avant de saisir les paramètres
                        </div>
                      )}
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr", gap:8 }}>
                        <div>
                          <div style={{ fontSize:10, color:GV.grisMid, marginBottom:2 }}>Épaisseur (m)</div>
                          <input type="number" min="0.1" step="0.5"
                            value={Math.round((parseFloat(c.profondeur_base) - parseFloat(c.profondeur_toit)) * 100) / 100}
                            onChange={e => {
                              const ep = parseFloat(e.target.value) || 0.5;
                              modifierCouche(c.id, "profondeur_base", parseFloat(c.profondeur_toit) + ep);
                            }}
                            style={{ width:"100%", padding:"4px 6px", fontSize:12, border:`1px solid ${GV.vert}`, borderRadius:5, outline:"none", boxSizing:"border-box" }}/>
                        </div>
                        <div>
                          <div style={{ fontSize:10, color:GV.grisMid, marginBottom:2 }}>γ (kN/m³)</div>
                          <input type="number" step="0.5" value={c.gamma}
                            onChange={e => modifierCouche(c.id,"gamma",parseFloat(e.target.value)||18)}
                            style={{ width:"100%", padding:"4px 6px", fontSize:12, border:`1px solid ${GV.border}`, borderRadius:5, outline:"none", boxSizing:"border-box" }}/>
                        </div>
                        <div>
                          <div style={{ fontSize:10, color:GV.grisMid, marginBottom:2 }}>c (kPa)</div>
                          <input type="number" step="1" value={c.cohesion}
                            onChange={e => modifierCouche(c.id,"cohesion",parseFloat(e.target.value)||0)}
                            style={{ width:"100%", padding:"4px 6px", fontSize:12, border:`1px solid ${GV.border}`, borderRadius:5, outline:"none", boxSizing:"border-box" }}/>
                        </div>
                        <div>
                          <div style={{ fontSize:10, color:GV.grisMid, marginBottom:2 }}>φ (°)</div>
                          <input type="number" step="1" value={c.phi}
                            onChange={e => modifierCouche(c.id,"phi",parseFloat(e.target.value)||0)}
                            style={{ width:"100%", padding:"4px 6px", fontSize:12, border:`1px solid ${GV.border}`, borderRadius:5, outline:"none", boxSizing:"border-box" }}/>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Coupe géologique dynamique */}
              <div style={{ flexShrink:0, width:300 }}>
                <label style={{ ...labelSt, marginBottom:12 }}>Coupe géologique</label>
                <div style={{ border:"2px solid #2c3e50", borderRadius:12, overflow:"hidden", boxShadow:"0 4px 20px rgba(0,0,0,0.12)", background:"#fff" }}>
                  <div style={{ background:"#2c3e50", padding:"8px 14px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <span style={{ color:"#fff", fontSize:12, fontWeight:700 }}>📐 SC-01 — Coupe lithostratigraphique</span>
                    <span style={{ color:"rgba(255,255,255,0.6)", fontSize:10 }}>Dynamique</span>
                  </div>
                  <CoupeGeologique
                    couches={couchesNormalisees}
                    nappe={["profonde","moderee","haute"].includes(form.nappe)}
                    nappe_profondeur={parseFloat(form.nappeProf) || 99}
                  />
                  <div style={{ background:"#f8f9fa", padding:"6px 14px", borderTop:"1px solid #e0e0e0" }}>
                    <span style={{ fontSize:10, color:GV.grisMid }}>Profondeur totale : <strong>{couchesNormalisees[couchesNormalisees.length-1]?.profondeur_base || 0} m</strong> · {couchesNormalisees.length} couche(s)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* VERIF-04 — Nature obligatoire sur toutes les couches */}
            {couches.some(c => !c.nature) && (
              <Alerte texte="VERIF-04 — Au moins une couche n'a pas de type de sol défini. Sélectionner la nature de chaque couche pour continuer." type="danger"/>
            )}

            {/* ══ BLOC 3 — Contexte Hydraulique & Géométrique ══ */}

            {/* Nappe */}
            <div style={{ marginBottom:24 }}>
              <label style={labelSt}>Nappe phréatique</label>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                <div>
                  <label style={labelSm}>Présence et niveau</label>
                  <select value={form.nappe} onChange={e=>inp("nappe",e.target.value)} style={{ ...inpSt, background:GV.blanc }}>
                    <option value="">Sélectionner...</option>
                    <option value="absente">Absente — confirmée par piézomètre</option>
                    <option value="profonde">Profonde (&gt; 5m)</option>
                    <option value="moderee">Modérée (2–5m)</option>
                    <option value="haute">Haute (&lt; 2m) ⚠️</option>
                    <option value="inconnue">Inconnue — à déterminer</option>
                  </select>
                </div>
                {form.nappe && form.nappe !== "absente" && form.nappe !== "" && (
                  <div>
                    <label style={labelSm}>
                      Profondeur de la nappe / TN (m)
                      {form.nappe !== "inconnue" && <span style={{ color:"#e53e3e" }}> *</span>}
                    </label>
                    <input
                      type="number" min="0" step="0.1"
                      value={form.nappeProf}
                      onChange={e => inp("nappeProf", e.target.value)}
                      placeholder={form.nappe === "inconnue" ? "Estimation pire cas (optionnel)" : form.nappe === "profonde" ? "ex: 6.00" : "ex: 3.50"}
                      style={{ ...inpSt, border:`1.5px solid ${form.nappeProf ? GV.vert : form.nappe === "inconnue" ? GV.border : GV.rouge}` }}
                    />
                    <div style={{ fontSize:11, color:GV.grisMid, marginTop:4 }}>
                      {form.nappe === "inconnue"
                        ? "Estimation conservative — pression hydrostatique + Co-Pilote GEO5 (Pressions des Terres)"
                        : "γw = 10 kN/m³ · utilisée pour la pression hydrostatique et GEO5 Stabilité des Pentes"}
                    </div>
                  </div>
                )}
              </div>
              {form.nappe === "haute" && (
                <Alerte texte="Nappe haute (< 2m) : pression hydrostatique active sur le parement. Prévoir drain géotextile + barbacanes. Réduire les paramètres φ et c (sol saturé)." type="warning"/>
              )}
              {form.nappe === "inconnue" && isSoutenement && (
                <Alerte texte="Nappe inconnue pour un mur de soutènement → supposer nappe haute jusqu'à preuve du contraire. Prévoir drain géotextile systématiquement." type="info"/>
              )}
            </div>

            {/* Profondeur D — toujours visible, label contextuel selon typeOuvrage */}
            <div style={{ marginBottom:24, background:"#F0FDF4", border:`1.5px solid ${GV.vertMid}40`, borderRadius:10, padding:"16px 18px" }}>
              <label style={{ ...labelSm, color:GV.vertDark }}>
                {labelProfondeurD}
                <span style={{ color:GV.rouge, marginLeft:3 }}>*</span>
                <span style={{ fontSize:11, fontWeight:400, color:GV.grisMid, marginLeft:8 }}>— profondeur de la base de l'ouvrage sous le terrain naturel</span>
              </label>
              <div style={{ display:"flex", alignItems:"flex-start", gap:20, flexWrap:"wrap" }}>
                <div>
                  <input
                    type="number" min="0.01" step="0.1"
                    value={form.profondeur_encastrement}
                    onChange={e => inp("profondeur_encastrement", e.target.value)}
                    placeholder="ex: 0.80"
                    style={{ width:140, padding:"9px 12px", fontSize:14, border:`1.5px solid ${dValide ? GV.vert : GV.rouge}`, borderRadius:8, outline:"none", background:"#fff" }}
                  />
                  {!dValide && (
                    <div style={{ marginTop:4, fontSize:12, color:GV.rouge, fontWeight:600 }}>⚠ Profondeur obligatoire (valeur en mètres, &gt; 0)</div>
                  )}
                  {dValide && form.hauteur && parseFloat(form.profondeur_encastrement) > parseFloat(form.hauteur) && (
                    <div style={{ marginTop:4, fontSize:12, color:GV.rouge, fontWeight:600 }}>⚠ D &gt; H — vérifier la cohérence</div>
                  )}
                  <div style={{ fontSize:11, color:GV.grisMid, marginTop:4 }}>
                    Couche d'assise à D dans la stratigraphie — détermine la solution recommandée
                  </div>
                </div>
                <div style={{ fontSize:12, color:"#374151", lineHeight:1.7, flex:1, minWidth:220 }}>
                  <div style={{ fontWeight:700, color:GV.vertDark, marginBottom:4 }}>⚠ Distinction critique :</div>
                  <div>• <strong>D</strong> = profondeur base fondation / TN → <em>paramètre de portance q<sub>adm</sub></em></div>
                  <div>• <strong>Épaisseur couche</strong> = données géologique → <em>stratigraphie uniquement</em></div>
                  <div style={{ color:GV.grisMid, marginTop:4 }}>D est transmis à la KB pour le calcul Terzaghi/Meyerhof.</div>
                </div>
              </div>
            </div>

            {/* Bâtiment voisin + distance — soutènement uniquement */}
            {showBatimentSensible && (
            <div style={{ marginBottom:24 }}>
              <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", fontSize:14, color:GV.dark, fontWeight:600 }}>
                <input type="checkbox" checked={form.batimentVoisin} onChange={e=>inp("batimentVoisin",e.target.checked)} style={{ width:18, height:18, accentColor:GV.vert }}/>
                Bâtiment ou infrastructure sensible à moins de 10m
              </label>
              <div style={{ fontSize:12, color:GV.grisMid, marginTop:4, marginLeft:28 }}>Déclenche vérification tassements différentiels + déplacements d'écran (δ_H ≤ H/500)</div>
              {form.batimentVoisin && (
                <Alerte texte="Bâtiment voisin sensible → vérification GEO5 Tassements obligatoire + déplacement d'écran ≤ H/500. Méthode Peck recommandée pour l'impact sur bâtiments adjacents." type="info"/>
              )}
              {(form.zoneUsage === "Milieu urbain (contrainte d'emprise)" || form.batimentVoisin) && (
                <div style={{ marginTop:12 }}>
                  <label style={labelSm}>
                    Distance du bâtiment le plus proche (m) <span style={{ color:"#e53e3e" }}>*</span>
                  </label>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <input
                      type="number" min="0" step="0.5"
                      value={form.batimentVoisinDistance}
                      onChange={e => inp("batimentVoisinDistance", e.target.value)}
                      placeholder="Ex : 2.5"
                      style={{ width:140, padding:"8px 12px", borderRadius:8, fontSize:14, border:`1.5px solid ${!form.batimentVoisinDistance ? "#e53e3e" : GV.vert}`, outline:"none", background:GV.blanc }}
                    />
                    <span style={{ fontSize:12, color:GV.grisMid }}>
                      Seuil ISS : 1.0 × H = {form.hauteur ? `${parseFloat(form.hauteur).toFixed(1)} m` : "H m"}
                    </span>
                  </div>
                  {form.batimentVoisinDistance && parseFloat(form.batimentVoisinDistance) < parseFloat(form.hauteur || 99) && (
                    <div style={{ marginTop:6, padding:"6px 12px", borderRadius:6, background:"#fff5f5", border:"1px solid #fed7d7", fontSize:12, color:"#c53030", fontWeight:600 }}>
                      ⚠ Distance &lt; H — Interaction Sol-Structure → Arbitre ISS activé
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* ══ Synthèse de la Résistance (RDT) ══ */}
            <div style={{ marginBottom:24, border:`2px solid ${GV.vertMid}`, borderRadius:12, padding:"20px 22px", background:"#FAFFFE" }}>
              <label style={{ ...labelSt, color:GV.vertDark, marginBottom:10 }}>
                Résistance de calcul R<sub>dt</sub> — Confirmation finale
                {(rdtIncohérent || rdtCisaillementIncohérent) && <span style={{ fontSize:13, marginLeft:8, color:"#B45309" }}>⚠️</span>}
              </label>
              <p style={{ fontSize:12, color:GV.grisMid, margin:"0 0 12px" }}>Conclusion de la page — les badges ci-dessous sont calculés depuis la stratigraphie (Bloc 1) et les essais (Bloc 2). Confirmez la tranche retenue.</p>

              {/* Badges de synthèse */}
              {coucheAssise && (
                <div style={{ display:"flex", gap:12, marginBottom:14, flexWrap:"wrap" }}>
                  {rdtTheorique && (
                    <div style={{ fontSize:12, background:"#EFF6FF", border:"1.5px solid #93C5FD", borderRadius:8, padding:"8px 14px", color:"#1E40AF", fontWeight:600 }}>
                      🧮 Terzaghi — c={coucheAssise.cohesion} kPa · φ={coucheAssise.phi}° · D={form.profondeur_encastrement||"0.8"}m
                      <span style={{ display:"block", fontSize:13, marginTop:2 }}>→ q<sub>adm</sub> ≈ <strong>{rdtTheorique} kPa</strong></span>
                    </div>
                  )}
                  {rdtSuggere && (
                    <div style={{ fontSize:12, background:"#F0FDF4", border:"1.5px solid #86EFAC", borderRadius:8, padding:"8px 14px", color:"#166534", fontWeight:600 }}>
                      💡 Corrélation Ménard — {coucheAssise.nature}
                      <span style={{ display:"block", fontSize:13, marginTop:2 }}>→ R<sub>dt</sub> ≈ <strong>{rdtSuggere} kPa</strong></span>
                    </div>
                  )}
                  {(form.pl_etoile || form.qc_cpt) && (
                    <div style={{ fontSize:12, background:"#FFF7ED", border:"1.5px solid #FCD34D", borderRadius:8, padding:"8px 14px", color:"#92400E", fontWeight:600 }}>
                      📐 Essais in-situ
                      {form.pl_etoile && <span style={{ display:"block", fontSize:13, marginTop:2 }}>pl*={form.pl_etoile} kPa → <strong>{rdtFromPl(form.pl_etoile)} kPa</strong></span>}
                      {form.qc_cpt && <span style={{ display:"block", fontSize:13, marginTop:2 }}>qc={form.qc_cpt} MPa → <strong>{rdtFromQc(form.qc_cpt)} kPa</strong></span>}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
                {RDT_OPTIONS.map(r => {
                  const isTheo = r.val === rdtTheorique;
                  const isSel  = form.rdt === r.val;
                  const nature = coucheAssise?.nature;
                  const desc   = (nature && RDT_DESC_NATURE[nature]?.[r.val]) || RDT_DESC_DEFAUT[r.val];
                  const cActif = r.val === "80" ? GV.rouge : r.val === "150" ? GV.orange : GV.vert;
                  return (
                    <div key={r.val} style={{ position:"relative" }}>
                      {isTheo && !isSel && (
                        <div style={{ position:"absolute", top:-9, left:"50%", transform:"translateX(-50%)", background:"#1D4ED8", color:"#fff", fontSize:9, fontWeight:700, borderRadius:4, padding:"2px 7px", whiteSpace:"nowrap", zIndex:1 }}>← Calculé</div>
                      )}
                      <BtnChoix valeur={r.val} label={r.label} desc={desc} champ="rdt" cActif={cActif}/>
                      {isTheo && isSel && (
                        <div style={{ textAlign:"center", fontSize:10, color:"#1D4ED8", fontWeight:700, marginTop:2 }}>✓ Cohérent c/φ</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {rdtCisaillementIncohérent && (
                <Alerte texte={`Attention : La résistance choisie (${form.rdt} kPa) semble en contradiction avec les paramètres de cisaillement du sol — Terzaghi donne q_adm ≈ ${rdtTheorique} kPa (c=${coucheAssise?.cohesion} kPa, φ=${coucheAssise?.phi}°). Vérifier ou justifier par essai in-situ (NF P94-500).`} type="warning"/>
              )}
              {rdtIncohérent && !rdtCisaillementIncohérent && (
                <Alerte texte={`Incohérence stratigraphique : Rdt=${form.rdt} kPa, mais la couche d'assise (${coucheAssise?.nature}) suggère ${rdtSuggere} kPa par corrélation Ménard. Justifier par PMT/CPT.`} type="warning"/>
              )}
              {form.rdt === "80" && !rdtCisaillementIncohérent && (
                <Alerte texte="Rdt < 100 kPa → fondations profondes (pieux, micropieux) quasi-obligatoires. Semelles superficielles insuffisantes." type="danger"/>
              )}
              {form.essais.includes("Aucun essai") && form.rdt && form.rdt !== "?" && (
                <Alerte texte={`Rdt=${form.rdt} kPa confirmé sans essai in-situ. Justification obligatoire : PMT (NF P94-110), CPT (NF P94-113) ou DTU 13.1. Un Rdt surestimé est la première cause de sinistre.`} type="danger"/>
              )}

              {/* Règle FR-S018 — Calcaire / Roche → fiche courte */}
              {regleFS018Active && (
                <div style={{ marginTop:14, background:"#EFF6FF", border:"2px solid #3B82F6", borderRadius:10, padding:"12px 16px", display:"flex", gap:12, alignItems:"flex-start" }}>
                  <span style={{ fontSize:18, flexShrink:0 }}>📐</span>
                  <div>
                    <div style={{ fontWeight:800, fontSize:12, color:"#1E40AF", textTransform:"uppercase", letterSpacing:0.5, marginBottom:4 }}>
                      Règle FR-S018 activée — Encastrement en fiche courte
                    </div>
                    <div style={{ fontSize:12, color:"#1E3A8A", lineHeight:1.7 }}>
                      Sol rocheux détecté (<strong>{coucheAssise.nature}</strong>) : les pieux peuvent être justifiés en <strong>fiche courte dans la roche</strong> (NF P94-262 §6.4 + Fascicule 62).
                      L'encastrement minimal requis est <strong>D ≥ 3 × diamètre</strong> dans la couche saine.
                    </div>
                    <div style={{ fontSize:11, color:"#3B82F6", marginTop:6 }}>
                      → Cette règle sera transmise à l'Analyse IA pour le dimensionnement des pieux.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── ÉTAPE 3 : CONTRAINTES ── */}
        {etape === 2 && (
          <div>
            <h2 style={{ fontSize:24, fontWeight:800, color:GV.dark, margin:"0 0 6px" }}>Contraintes du projet</h2>
            <p style={{ fontSize:14, color:GV.grisMid, margin:"0 0 28px" }}>Ces paramètres permettent à l'IA de comparer les coûts et délais de chaque solution.</p>

            {/* Récap sismique */}
            {form.zoneSismique && (
              <div style={{ background:GV.vertLight, border:`1.5px solid ${GV.vertMid}`, borderRadius:10, padding:"14px 18px", marginBottom:24 }}>
                <div style={{ fontSize:12, fontWeight:700, color:GV.vertDark, marginBottom:6 }}>📋 RÉCAPITULATIF — DONNÉES SAISIES</div>
                <div style={{ fontSize:13, color:GV.dark, lineHeight:1.8 }}>
                  Zone sismique : <strong>{form.zoneSismique}</strong> · A = <strong>{KH_MAP[form.zoneSismique]}g</strong> · Kh = 1/3×A×I×S
                  {form.surcharge && <> · Surcharge : <strong>{form.surcharge} kN/m²</strong></>}
                  {form.rdt && form.rdt !== "?" && <> · Rdt estimé : <strong>{form.rdt} kPa</strong></>}
                  {form.nappe && <> · Nappe : <strong>{form.nappe}</strong></>}
                  {alerteSurchargeSeisme && <span style={{ color:GV.orange, fontWeight:700, marginLeft:8 }}>⚠️ Combinaison critique</span>}
                </div>
              </div>
            )}

            {/* Espace + Délai */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:24 }}>
              <div>
                <label style={labelSm}>
                  Espace disponible / emprise
                  {espaceAutoDetecte && form.espaceDisponible === espaceAutoDetecte && (
                    <span style={{ fontSize:10, marginLeft:8, background:"#1D4ED8", color:"#fff", borderRadius:4, padding:"1px 6px", fontWeight:700, textTransform:"none" }}>Auto-détecté</span>
                  )}
                </label>
                <select
                  value={form.espaceDisponible}
                  onChange={e => inp("espaceDisponible", e.target.value)}
                  style={{ ...inpSt, background:GV.blanc, border:`1.5px solid ${espaceManquant ? "#DC2626" : espaceContradiction ? GV.orange : GV.border}` }}
                >
                  <option value="">Sélectionner...</option>
                  <option value="contraint">Très contraint (&lt; 1m)</option>
                  <option value="limite">Limité (1–3m)</option>
                  <option value="normal">Normal (3–8m)</option>
                  <option value="large">Large (&gt; 8m)</option>
                </select>
                {espaceAutoDetecte && form.espaceDisponible === espaceAutoDetecte && (
                  <div style={{ fontSize:11, color:"#1E40AF", marginTop:4, display:"flex", alignItems:"center", gap:4 }}>
                    ℹ️ Espace limité auto-détecté — distance bâtiment voisin : <strong>{distVoisin} m</strong> &lt; {espaceAutoDetecte === "contraint" ? "1 m" : `${hauteurOuvrage.toFixed(1)} m (H)`}
                  </div>
                )}
                {espaceContradiction && (
                  <div style={{ fontSize:11, color:GV.orange, marginTop:4, fontWeight:600, display:"flex", alignItems:"center", gap:4 }}>
                    ⚠️ Contradiction : distance bâtiment = {distVoisin} m suggère <strong>"{espaceAutoDetecte}"</strong> — vérifier la cohérence avant analyse
                  </div>
                )}
                {espaceManquant && (
                  <div style={{ fontSize:11, color:"#DC2626", marginTop:4, fontWeight:600, display:"flex", alignItems:"center", gap:4 }}>
                    ⚠️ Espace requis pour ce type d'ouvrage — sélectionner une valeur pour analyser
                  </div>
                )}
              </div>
              <div>
                <label style={labelSm}>Délai de réalisation souhaité</label>
                <select value={form.delai} onChange={e=>inp("delai",e.target.value)} style={{ ...inpSt, background:GV.blanc }}>
                  <option value="">Sélectionner...</option>
                  <option value="urgent">Urgent (&lt; 1 mois)</option>
                  <option value="normal">Normal (1–3 mois)</option>
                  <option value="flexible">Flexible (&gt; 3 mois)</option>
                </select>
              </div>
            </div>

            {/* Contraintes légales et vibrations */}
            {(showServitudeTrefonds || showAvoisinantsSensibles) && (
            <div style={{ marginBottom:24, border:`1.5px solid ${GV.border}`, borderRadius:10, padding:"18px 20px", background:GV.blanc }}>
              <div style={{ fontSize:13, fontWeight:700, color:GV.dark, marginBottom:14 }}>Contraintes légales et environnementales</div>

              {showServitudeTrefonds && (
              <div style={{ marginBottom:14 }}>
                <label style={labelSm}>Servitude de tréfonds (Tirants d'ancrage)</label>
                <select
                  value={form.servitudeTrefonds}
                  onChange={e => inp("servitudeTrefonds", e.target.value)}
                  style={{ ...inpSt, background:GV.blanc, border:`1.5px solid ${form.servitudeTrefonds === "refusee" ? GV.rouge : GV.border}` }}
                >
                  <option value="inconnue">À l'étude / Inconnue</option>
                  <option value="accordee">Accordée</option>
                  <option value="refusee">Refusée</option>
                </select>
                {form.servitudeTrefonds === "refusee" && (
                  <div style={{ marginTop:6, padding:"6px 12px", borderRadius:6, background:"#fff5f5", border:"1px solid #fed7d7", fontSize:12, color:"#c53030", fontWeight:600 }}>
                    Écran ancré exclu — tirants hors emprise légalement bloqués (NF P94-282)
                  </div>
                )}
              </div>
              )}

              {showAvoisinantsSensibles && (
              <div>
                <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", fontSize:14, color:GV.dark, fontWeight:600 }}>
                  <input
                    type="checkbox"
                    checked={form.avoisinantsSensibles}
                    onChange={e => inp("avoisinantsSensibles", e.target.checked)}
                    style={{ width:18, height:18, accentColor:GV.rouge }}
                  />
                  Avoisinants très sensibles (Vibrations)
                </label>
                <div style={{ fontSize:12, color:GV.grisMid, marginTop:4, marginLeft:28 }}>Hôpital, bâti fragile, monument — battage et vibrofonçage proscrits</div>
                {form.avoisinantsSensibles && (
                  <div style={{ marginTop:8, padding:"8px 12px", borderRadius:6, background:"#fff5f5", border:"1px solid #fed7d7", fontSize:12, color:"#c53030", fontWeight:600, marginLeft:28 }}>
                    Palplanches et pieux battus exclus — ondes de choc incompatibles avec les avoisinants
                  </div>
                )}
              </div>
              )}
            </div>
            )}

            {/* Budget */}
            <div style={{ background:`linear-gradient(135deg,${GV.vertLight},#f0fdf4)`, border:`1.5px solid ${GV.vertMid}40`, borderRadius:12, padding:"20px 24px" }}>
              <label style={{ fontSize:13, fontWeight:700, color:GV.vertDark, display:"block", marginBottom:6 }}>💰 Budget indicatif de l'ouvrage (EUR / ml) — optionnel</label>
              <p style={{ fontSize:12, color:GV.grisMid, margin:"0 0 12px", lineHeight:1.6 }}>Coût de construction de <strong>l'ouvrage géotechnique</strong> — permet à l'IA de comparer les solutions.</p>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                {(pays === "france"
                  ? ["< 500 €/ml","500 – 2 000 €/ml","2 000 – 5 000 €/ml","+ 5 000 €/ml","Pas de contrainte"]
                  : ["< 50 000 DZD/ml","50 000 – 150 000 DZD/ml","150 000 – 500 000 DZD/ml","+ 500 000 DZD/ml","Pas de contrainte"]
                ).map(b => (
                  <button key={b} onClick={()=>inp("budget",b)} style={{ padding:"8px 16px", borderRadius:8, fontSize:12, cursor:"pointer", border:`1.5px solid ${form.budget===b?GV.vert:GV.border}`, background:form.budget===b?GV.vert:GV.blanc, color:form.budget===b?GV.blanc:GV.dark, fontWeight:form.budget===b?700:400 }}>{b}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* NAVIGATION */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:36, paddingTop:24, borderTop:`1px solid ${GV.border}` }}>
          <button onClick={()=>allerEtape(Math.max(0,etape-1))} disabled={etape===0} style={{ background:"transparent", border:`1.5px solid ${GV.border}`, borderRadius:8, padding:"12px 24px", fontSize:14, cursor:etape>0?"pointer":"not-allowed", color:etape>0?GV.dark:GV.grisMid, fontWeight:600, opacity:etape>0?1:0.4 }}>← Retour</button>
          <div style={{ display:"flex", gap:8 }}>
            {ETAPES.map((_,i) => <div key={i} style={{ width:etape===i?24:8, height:8, borderRadius:4, background:etape===i?GV.vert:etape>i?GV.vertClair:GV.border, transition:"all 0.3s" }}/>)}
          </div>
          {etape < 2 ? (
            <button onClick={()=>canNext()&&allerEtape(etape+1)} style={{ background:canNext()?`linear-gradient(135deg,${GV.vert},${GV.vertClair})`:GV.border, border:"none", borderRadius:8, padding:"12px 32px", fontSize:14, fontWeight:800, cursor:canNext()?"pointer":"not-allowed", color:canNext()?GV.blanc:GV.grisMid, boxShadow:canNext()?`0 4px 16px ${GV.vert}40`:"none" }}>{t("pageprojet.btn_suivant")}</button>
          ) : (
            <button onClick={() => {
              if (!canNext()) return;
              const couchesPropres = couchesNormalisees.map(
                ({ typeSol: _ts, type_sol: _ts2, sol: _s, ...c }) => c
              );
              const data = {
                ...form,
                couches: couchesPropres,
                essais: form.essais.map(e => ESSAIS_NORM[e] || e),
                typeSol: couchesPropres[0]?.nature || "",
              };
              if (onAnalyser) {
                onAnalyser(data);
              } else {
                setDonneesProjet && setDonneesProjet(data);
                window.scrollTo({ top:0, behavior:"smooth" });
                onEnter && onEnter("solution");
              }
            }} style={{ background: canNext() ? `linear-gradient(135deg,${GV.vert},${GV.vertClair})` : GV.border, border:"none", borderRadius:8, padding:"14px 36px", fontSize:15, fontWeight:800, cursor: canNext() ? "pointer" : "not-allowed", color: canNext() ? GV.blanc : GV.grisMid, boxShadow: canNext() ? `0 4px 20px ${GV.vert}50` : "none" }}>{t("pageprojet.btn_analyser")}</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PageProjet;
